// Shared data shapes for the whole lab.

export interface Stats {
  mean: number;
  median: number;
  min: number;
  max: number;
  std: number;
  cv: number;
  count: number;
}

export interface TestResult {
  name: string;
  group: string;
  success: boolean;
  durationMs: number;
  error?: string;
  samples?: number[];
  stats?: Stats;
  ratios?: Record<string, number>;
  metadata?: Record<string, unknown>;
}

export interface GroupResult {
  group: string;
  durationMs: number;
  tests: TestResult[];
}

export interface LabReport {
  version: string;
  mode: 'full' | 'prod';
  startedAt: string;
  finishedAt: string;
  totalDurationMs: number;
  environment: Record<string, unknown>;
  groups: GroupResult[];
  summary: {
    totalTests: number;
    passedTests: number;
    failedTests: number;
  };
  label?: string;
  cpuModel?: string;
}

export interface VisibilityState {
  hiddenAtStart: boolean;
  hiddenDuringRun: boolean;
  transitions: number;
  hiddenTimeMs: number;
  focusedAtStart: boolean;
  focusedDuringRun: boolean;
}

export interface LabConfig {
  mode: 'full' | 'prod';
  debug: boolean;
  endpoint: string;
  enableWebGL: boolean;
  enableWebGL2: boolean;
  enableWASM: boolean;
  enableWorkers: boolean;
  enableNetwork: boolean;
  enableAudio: boolean;
  enableSensors: boolean;
  cacheMode?: 'fast' | 'research';
  // Research tuning
  cacheRuns?: number;
  cacheWarmup?: number;
  cacheIterations?: number;
  cacheSizes?: number[];
  cacheAssociativities?: number[];
  cachePageCounts?: number[];
  visibilityTracker?: { getState(): VisibilityState; destroy(): void };
  workerScalingIterations?: number;
  workerScalingRuns?: number;
  workerOverheadSamples?: number;
  workerContentionIterations?: number;
  workerContentionRuns?: number;
  memoryBandwidthSizes?: number[];
  memoryLatencySizes?: number[];
}

export const DEFAULT_CONFIG: LabConfig = {
  mode: 'full',
  debug: false,
  endpoint: '/api/collect',
  enableWebGL: true,
  enableWebGL2: true,
  enableWASM: true,
  enableWorkers: true,
  enableNetwork: true,
  enableAudio: true,
  enableSensors: true,
  cacheMode: 'research',
};

export const PROD_CONFIG: Partial<LabConfig> = {
  mode: 'prod',
  enableNetwork: false,
  enableAudio: false,
  enableSensors: false,
  enableWorkers: false,
  cacheMode: 'fast',
  cacheSizes: [1024 * 1024, 8 * 1024 * 1024],
  cacheAssociativities: [4],
  cachePageCounts: [8],
  cacheIterations: 150_000,
  cacheRuns: 1,
  cacheWarmup: 0,
  memoryBandwidthSizes: [8, 64],
  memoryLatencySizes: [1024 * 1024],
};

export interface TimingOptions {
  runs?: number;
  warmup?: number;
}

export interface AllocationResult {
  size: number;
  success: boolean;
  durationMs: number;
  error?: string;
}
